import React, { useState } from 'react';
import { ConfigProvider } from 'antd';
import ReactMarkdown from 'react-markdown';
import './ViewMethodology.css';
import { useTheme } from '../../theme/ThemeContext';
import GitBranch from '../../atoms/Icon/GitBranch';
import Chevron from '../../atoms/Icon/Chevron';

export interface MethodologySection {
    /** Section title (e.g., "Filters Applied", "Exclusions") */
    title: string;
    /** Array of items in this section */
    items: string[];
}

export interface ViewMethodologyProps {
    /** Title for the methodology section */
    title?: string;
    /** Array of methodology sections */
    sections?: MethodologySection[];
    /** Formula(s) or calculation note(s) displayed as a list in the Formulae section */
    formula?: string | string[];
    /** SQL query (or queries) to render as code blocks in the Methodology tab */
    sqlQuery?: string | string[];
    /** Content for the Data Source tab (e.g. tables used, sample rows). Shows a placeholder when omitted. */
    dataSourceContent?: React.ReactNode;
    /** Whether the methodology section is initially expanded */
    defaultExpanded?: boolean;
    /** Custom icon to display (defaults to GitBranch) */
    icon?: React.ReactNode;
    /** Callback when expand/collapse state changes */
    onExpandChange?: (expanded: boolean) => void;
    /** Static expanded card for PDF/DOCX capture (no toggle; matches export styling). */
    forExport?: boolean;
    /** Data test attribute */
    'data-test'?: string;
    /** Data test ID attribute */
    'data-test-id'?: string;
}

type MethodologyTab = 'methodology' | 'dataSource';

const ViewMethodology: React.FC<ViewMethodologyProps> = (props) => {
    const {
        title = 'View Methodology',
        sections = [],
        formula,
        sqlQuery,
        dataSourceContent,
        defaultExpanded = true,
        icon,
        onExpandChange,
        forExport = false,
        'data-test-id': dataTestId,
    } = props;

    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    const [activeTab, setActiveTab] = useState<MethodologyTab>('methodology');
    const theme = useTheme();
    const showContent = forExport || isExpanded;
    const sqlQueries = sqlQuery == null ? [] : Array.isArray(sqlQuery) ? sqlQuery : [sqlQuery];

    const handleToggle = () => {
        const newExpanded = !isExpanded;
        setIsExpanded(newExpanded);
        onExpandChange?.(newExpanded);
    };

    const formulaItems = formula
        ? Array.isArray(formula)
            ? formula
            : [formula]
        : [];
    const allSections = [
        ...sections,
        ...(formulaItems.length > 0 ? [{ title: 'Formulae', items: formulaItems }] : []),
    ];

    const methodologySections = (
        <div className="methodology-content">
            {allSections.map((section, sectionIndex) => (
                <section key={`${section.title}-${sectionIndex}`} className="methodology-section">
                    <h3 className="methodology-section-title">{section.title}</h3>
                    <ul className="methodology-section-list">
                        {section.items.map((item, itemIndex) => (
                            <li key={`${sectionIndex}-${itemIndex}`}>
                                <ReactMarkdown
                                    allowedElements={['p', 'strong', 'em', 'code', 'br']}
                                    unwrapDisallowed
                                    components={{ p: ({ children }) => <span>{children}</span> }}
                                >
                                    {item}
                                </ReactMarkdown>
                            </li>
                        ))}
                    </ul>
                </section>
            ))}

            {sqlQueries.length > 0 && (
                <section className="methodology-section">
                    <h3 className="methodology-section-title">SQL Query</h3>
                    {sqlQueries.map((query, idx) => (
                        <pre
                            key={idx}
                            data-test-id={dataTestId ? `${dataTestId}-sql-query-${idx}` : undefined}
                            className="methodology-sql-query"
                        >
                            <code>{query}</code>
                        </pre>
                    ))}
                </section>
            )}
        </div>
    );

    const tabStrip = !forExport && (
        <div className="flex items-center gap-1 bg-[#f4f4f5] rounded-md p-1" role="tablist" aria-label="Methodology views">
            <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'methodology'}
                onClick={() => setActiveTab('methodology')}
                data-test-id={dataTestId ? `${dataTestId}-tab-methodology` : undefined}
                className={
                    activeTab === 'methodology'
                        ? 'font-sans text-[13px] leading-[18px] text-[#1f1f1f] bg-white border border-[#e4e4e7] border-solid rounded-md px-3 py-1 cursor-pointer'
                        : 'font-sans text-[13px] leading-[18px] text-[#71717a] bg-transparent border-none rounded-md px-3 py-1 cursor-pointer'
                }
                style={{ fontWeight: 400 }}
            >
                Methodology
            </button>
            <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'dataSource'}
                onClick={() => setActiveTab('dataSource')}
                data-test-id={dataTestId ? `${dataTestId}-tab-data-source` : undefined}
                className={
                    activeTab === 'dataSource'
                        ? 'font-sans text-[13px] leading-[18px] text-[#1f1f1f] bg-white border border-[#e4e4e7] border-solid rounded-md px-3 py-1 cursor-pointer'
                        : 'font-sans text-[13px] leading-[18px] text-[#71717a] bg-transparent border-none rounded-md px-3 py-1 cursor-pointer'
                }
                style={{ fontWeight: 400 }}
            >
                Data Source
            </button>
        </div>
    );

    const dataSourceTab = (
        <div className="flex items-start w-full min-w-0" data-test-id={dataTestId ? `${dataTestId}-data-source` : undefined}>
            {dataSourceContent ?? (
                <p className="font-sans text-[14px] leading-[22px] text-[#71717a] m-0">
                    No data source information available for this result.
                </p>
            )}
        </div>
    );

    return (
        <ConfigProvider theme={theme}>
            <div
                className="bg-[#fafafa] flex flex-col gap-4 items-start px-4 py-3 pb-4 rounded-lg w-full min-w-0"
                data-test={props['data-test']}
                data-test-id={props['data-test-id']}
                data-view-methodology
            >
                <div className="flex flex-col gap-3 items-start w-full">
                    {forExport ? (
                        <div className="flex items-center gap-3 w-full">
                            {icon || <GitBranch size={24} color="#1f1f1f" />}
                            <p
                                className="font-sans text-[14px] leading-[22px] text-[#1f1f1f] m-0"
                                style={{ fontWeight: 600 }}
                            >
                                {title}
                            </p>
                        </div>
                    ) : (
                        <div className="flex items-center justify-between gap-3 w-full min-w-0">
                            <div className="flex items-center gap-3 min-w-0">
                                {icon || <GitBranch size={24} color="#1f1f1f" />}
                                {showContent ? tabStrip : <span className="text-[14px]">{title}</span>}
                            </div>
                            <button
                                type="button"
                                className="cursor-pointer bg-transparent border-none p-2"
                                onClick={handleToggle}
                                aria-expanded={isExpanded}
                                aria-label={isExpanded ? `Collapse ${title}` : `Expand ${title}`}
                            >
                                <Chevron size={12} color="#71717a" rotate={isExpanded ? 0 : 180} />
                            </button>
                        </div>
                    )}

                    {showContent && <div className="h-[1px] bg-[#e4e4e7] w-full" />}
                </div>

                {showContent && (forExport || activeTab === 'methodology') && (
                    <>{methodologySections}</>
                )}

                {showContent && !forExport && activeTab === 'dataSource' && dataSourceTab}
            </div>
        </ConfigProvider>
    );
};

export default ViewMethodology;
